<template>
  <FlugstreckenVergleichLayout
    :ist-title="computedIstTitle"
    :soll-title="computedSollTitle"
    :status-pruefung="flugstrecke.statusPruefung"
    :status-tickets="flugstrecke.statusTickets"
  >
    <!-- Ist Content -->
    <template #ist-content>
      <div
        class="mt-7"
        v-for="segment in flugstrecke.segmente"
        :key="flugstrecke.bnr + '-' + flugstrecke.nummer + '-' + segment.segmentNummer"
      >
        <div
          class="d-flex align-items-center"
          :style="
            segment.segmentStatus === 'UN' || segment.segmentStatus === 'TK'
              ? 'border: 1px solid #FFA800; border-radius: 12px; padding: 4px'
              : ''
          "
        >
          <!-- Abflughäfen - Seite -->
          <div class="d-flex flex-column" style="width: 140px">
            <div class="mb-2 grey500" style="text-align: center">
              {{ segment.abflughafen }}
            </div>
            <div class="d-flex justify-content-center grey400" style="gap: 3px; align-items: center">
              <PlaneTakeoff
                :size="17"
                :class="[
                  (segment.abflughafen === flugstrecke.reiseterminFlugstrecke.abflughafen ||
                  flugstrecke.reiseterminFlugstrecke.backupAbflughaefen.includes(segment.abflughafen))
                  && segment === flugstrecke.segmente[0]
                    ? restriktionenOutOfWindow
                      ? 'icon-warning'
                      : 'icon-success'
                    : '',
                ]"
              />
              {{ formatDate(segment.abflugsdatum) }}
            </div>
          </div>

          <!-- Div aus Ticket icon - Dashed Line - Betreiber und Flugnummer -->
          <div style="flex-grow: 1; display: flex; flex-direction: column; align-items: center">
            <div>
              <Tickets v-if="segment.buchungsstatus === 'TicketIssued'" :size="16" class="text-success" />
              <Tickets v-else :size="16" class="text-danger" />
            </div>

            <!-- Dashed line -->
            <div class="horizontal-line w-100"></div>

            <div class="mt-4 grey400">
              {{ segment.betreiber + '' + segment.flugnummer }}
            </div>
          </div>

          <!-- Zielflughäfen - Seite -->
          <div class="d-flex flex-column" style="width: 140px">
            <div class="mb-2 grey500" style="text-align: center">
              {{ segment.zielflughafen }}
            </div>
            <div class="d-flex justify-content-center grey400" style="gap: 3px; align-items: center">
              <PlaneLanding
                :size="17"
                :class="[
                  (segment.zielflughafen === flugstrecke.reiseterminFlugstrecke.zielflughafen ||
                  flugstrecke.reiseterminFlugstrecke.backupZielflughaefen.includes(segment.zielflughafen))
                  && segment === flugstrecke.segmente[flugstrecke.segmente.length -1]
                    ? restriktionenOutOfWindow
                      ? 'icon-warning'
                      : 'icon-success'
                    : '',
                ]"
              />
              {{ formatDate(segment.ankunftsdatum) }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="flugstrecke.segmente.length === 0" class="mt-4">
        <b style="color: rgba(0, 0, 0, 0.5)">
          <div v-if="isEigenanreise">Der Teilnehmer hat mit Eigenanreise gebucht.</div>
          <div v-else>Keine Segmente gematched.</div>
        </b>
      </div>
    </template>

    <!-- Soll Content -->
    <template v-if="showSollContent" #soll-content>
      <div class="d-flex align-items-center w-100" :class="art === 'Hinflug' ? 'mt-auto' : ''">
        <!-- Flughäfen -->
        <div class="d-flex flex-column mt-7">
          <!-- Abflughäfen - Seite -->
          <div class="d-flex" style="gap: 3px; justify-content: center; align-items: center">
            <div class="mb-2 grey500" style="text-align: center">
              {{ flugstrecke.reiseterminFlugstrecke.abflughafen }}
            </div>
            <div
              class="grey500 mb-2"
              v-if="flugstrecke.reiseterminFlugstrecke.backupAbflughaefen.length > 0"
              style="text-align: center"
            >
              {{ `(${flugstrecke.reiseterminFlugstrecke.backupAbflughaefen.join(', ')})` }}
            </div>
          </div>

          <div
            class="d-flex flex-column align-items-center grey400"
            style="gap: 3px; align-items: center"
            v-if="restriktionenRueckValid"
          >
            <div>{{ formatDate(flugstrecke.reiseterminFlugstrecke.fruehesterAbflug) }}</div>
            <div>- {{ formatDate(flugstrecke.reiseterminFlugstrecke.spaetesterAbflug) }}</div>
            <div
              v-if="
                art == 'Rückflug' &&
                (verlaengerungen.length != 0 || teilnehmerNachverlaengerungen.length != 0)
              "
            >
              <b-badge :id="'vl' + teilnehmer.name + '-' + teilnehmer.vorname">VL</b-badge>
              <b-tooltip
                :target="'vl' + teilnehmer.name + '-' + teilnehmer.vorname"
                triggers="hover"
                custom-class="custom-tooltip"
              >
                <div
                  v-for="vl in verlaengerungenSorted"
                  :key="
                    'nachverlaengerung' +
                    '-' +
                    vl.kuerzel +
                    '-' +
                    vl.startdatum +
                    '-' +
                    vl.zimmertyp +
                    '-' +
                    teilnehmer.name +
                    '-' +
                    teilnehmer.vorname
                  "
                >
                  <b>{{ vl.kuerzel }}:</b>
                  <br />
                  {{
                    `${formatDateWithoutHours(vl.startdatum)} - ${formatDateWithoutHours(
                      addDays(new Date(vl.startdatum), vl.dauer)
                    )}`
                  }}
                </div>
                <div
                  v-for="teilnehmerVerlaengerung in teilnehmerNachverlaengerungen"
                  :key="
                    'nachverlaengerung-teilnehmer' +
                    '-' +
                    teilnehmerVerlaengerung.von +
                    '-' +
                    teilnehmerVerlaengerung.bis +
                    '-' +
                    teilnehmer.name +
                    '-' +
                    teilnehmer.vorname
                  "
                >
                  <b>{{ teilnehmerVerlaengerung.teilnehmerVerlaengerungTyp }}:</b>
                  <br />
                  {{ formatDateWithoutHours(teilnehmerVerlaengerung.von) }} -
                  {{ formatDateWithoutHours(teilnehmerVerlaengerung.bis) }}
                </div>
              </b-tooltip>
            </div>
          </div>
        </div>

        <div class="horizontal-line w-100"></div>

        <div class="d-flex flex-column mt-7">
          <!-- Zielflughäfen - Seite -->
          <div class="d-flex" style="gap: 3px; justify-content: center; align-items: center">
            <div class="mb-0 grey500" style="text-align: center">
              {{ flugstrecke.reiseterminFlugstrecke.zielflughafen }}
            </div>
            <div
              class="grey500 mb-0"
              v-if="flugstrecke.reiseterminFlugstrecke.backupZielflughaefen.length > 0"
              style="text-align: center"
            >
              {{ `(${flugstrecke.reiseterminFlugstrecke.backupZielflughaefen.join(', ')})` }}
            </div>
          </div>

          <div
            class="d-flex flex-column align-items-center mt-2 grey400"
            style="gap: 3px; align-items: center"
            v-if="restriktionenHinValid"
          >
            <div>
              {{ formatDate(flugstrecke.reiseterminFlugstrecke.fruehesteAnkunft) }}
            </div>
            <div>- {{ formatDate(flugstrecke.reiseterminFlugstrecke.spaetesteAnkunft) }}</div>
            <div
              v-if="
                art == 'Hinflug' &&
                (vorverlaengerungen.length != 0 || teilnehmerVorverlaengerungen.length != 0)
              "
            >
              <b-badge :id="'vvl' + teilnehmer.name + '-' + teilnehmer.vorname">VVL</b-badge>
              <b-tooltip
                :target="'vvl' + teilnehmer.name + '-' + teilnehmer.vorname"
                triggers="hover"
                custom-class="custom-tooltip"
              >
                <div
                  v-for="vvl in vorverlaengerungenSorted"
                  :key="
                    'vorverlaengerung' +
                    '-' +
                    vvl.kuerzel +
                    '-' +
                    vvl.startdatum +
                    '-' +
                    vvl.zimmertyp +
                    '-' +
                    teilnehmer.name +
                    '-' +
                    teilnehmer.vorname
                  "
                >
                  <b>{{ vvl.kuerzel }}:</b>
                  <br />
                  {{
                    `${formatDateWithoutHours(vvl.startdatum)} - ${formatDateWithoutHours(
                      addDays(new Date(vvl.startdatum), vvl.dauer)
                    )}`
                  }}
                </div>
                <div
                  v-for="teilnehmerVorverlaengerung in teilnehmerVorverlaengerungen"
                  :key="
                    'vorverlaengerung-teilnehmer' +
                    '-' +
                    teilnehmerVorverlaengerung.von +
                    '-' +
                    teilnehmerVorverlaengerung.bis +
                    '-' +
                    teilnehmer.name +
                    '-' +
                    teilnehmer.vorname
                  "
                >
                  <b>{{ teilnehmerVorverlaengerung.teilnehmerVerlaengerungTyp }}:</b>
                  <br />
                  {{ formatDateWithoutHours(teilnehmerVorverlaengerung.von) }} -
                  {{ formatDateWithoutHours(teilnehmerVorverlaengerung.bis) }}
                </div>
              </b-tooltip>
            </div>
          </div>
        </div>
      </div>
    </template>
  </FlugstreckenVergleichLayout>
</template>

<script>
import { addDays, format, compareAsc } from 'date-fns';
import { formatDateWithoutHours } from '@/core/common/helpers/utils';
import FlugstreckenVergleichLayout from './flugstrecken-vergleich-layout.vue';
import { PlaneTakeoff, PlaneLanding, Tickets } from 'lucide-vue';

export default {
  components: { FlugstreckenVergleichLayout, PlaneTakeoff, PlaneLanding, Tickets },
  props: {
    flugstrecke: { type: Object },
    art: { type: String, required: true },
    verlaengerungen: { type: Array },
    vorverlaengerungen: { type: Array },
    teilnehmerVerlaengerungen: { type: Array },
    teilnehmer: { type: Object },
    showSollContent: { type: Boolean, default: true },
    istTitle: { type: String, default: null },
    sollTitle: { type: String, default: null },
  },
  computed: {
    computedIstTitle() {
      return this.istTitle || `${this.art} Ist`;
    },
    computedSollTitle() {
      return this.sollTitle || `${this.art} Soll`;
    },
    verlaengerungenSorted() {
      return this.verlaengerungen
        .slice()
        .sort((a, b) => compareAsc(new Date(a.startdatum), new Date(b.startdatum)));
    },
    vorverlaengerungenSorted() {
      return this.vorverlaengerungen
        .slice()
        .sort((a, b) => compareAsc(new Date(a.startdatum), new Date(b.startdatum)));
    },
    isEigenanreise() {
      if (this.teilnehmer.eigenanreiseArt == null) return false;

      const eigenanreiseFlags = this.teilnehmer.eigenanreiseArt.split(',').map(e => e.trim());

      return (
        eigenanreiseFlags.includes('EAF') ||
        eigenanreiseFlags.includes('EA') ||
        eigenanreiseFlags.includes('EF')
      );
    },
    teilnehmerVorverlaengerungen() {
      const teilnehmerVorverlaengerungen = this.teilnehmerVerlaengerungen
        .filter(tVL => tVL.istVorverlaengerung)
        .sort((a, b) => compareAsc(new Date(a.von), new Date(b.von)));
      return teilnehmerVorverlaengerungen;
    },
    teilnehmerNachverlaengerungen() {
      const teilnehmerNachverlaengerungen = this.teilnehmerVerlaengerungen
        .filter(tVL => tVL.istVorverlaengerung == false)
        .sort((a, b) => compareAsc(new Date(a.von), new Date(b.von)));
      return teilnehmerNachverlaengerungen;
    },
    restriktionenHinValid() {
      return (
        this.flugstrecke.reiseterminFlugstrecke.fruehesteAnkunft != null &&
        this.flugstrecke.reiseterminFlugstrecke.spaetesteAnkunft != null
      );
    },
    restriktionenRueckValid() {
      return (
        this.flugstrecke.reiseterminFlugstrecke.fruehesterAbflug != null &&
        this.flugstrecke.reiseterminFlugstrecke.spaetesterAbflug != null
      );
    },
    verlaengerungenDauer() {
      return this.verlaengerungen.reduce((acc, curr) => curr.dauer + acc, 0);
    },
    vorverlaengerungenDauer() {
      return this.vorverlaengerungen.reduce((acc, curr) => curr.dauer + acc, 0);
    },
    restriktionenOutOfWindow() {
      return this.flugstrecke.statusPruefung !== 'AllGood';
    },
  },
  methods: {
    addDays,
    formatDate(date) {
      if (!date) return '-';

      return format(new Date(date.slice(0, -1)), 'dd.MM.yy HH:mm');
    },
    formatDateWithoutHours,
  },
};
</script>

<style scoped>
.tooltip {
  opacity: 1 !important;
}

.horizontal-line {
  border-top: 1px dashed #c5ced6;
  height: 0;
  flex-grow: 1;
  margin-left: 8px;
  margin-right: 8px;
}

.grey400 {
  font-weight: 400;
  color: var(--text-body);
}

.grey500 {
  font-weight: 500;
  color: var(--text-body);
  font-size: 20px;
}

.icon-success {
  stroke: var(--success);
  fill: var(--success);
}

.icon-warning {
  stroke: var(--warning);
  fill: var(--warning);
}
</style>
